import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, LogOut, Check, X, ChevronDown, ChevronUp } from "lucide-react";

interface AdminEvent {
  id: number;
  title: string;
  description: string;
  date: string;
  imageUrl: string;
  isUpcoming: boolean;
  type: string;
}

interface BanquetRequest {
  id: number;
  name: string;
  phone: string;
  eventType: string;
  date: string;
  guests: number;
  comment: string | null;
  createdAt: string;
}

interface Vacancy {
  id: number;
  title: string;
  description: string;
  requirements: string;
  salary: string;
  isActive: boolean;
  createdAt: string;
}

interface VacancyApplication {
  id: number;
  vacancyId: number | null;
  vacancyTitle: string;
  name: string;
  phone: string;
  email: string;
  comment: string;
  status: string;
  createdAt: string;
}

type FormData = Omit<AdminEvent, "id">;
type VacancyForm = { title: string; description: string; requirements: string; salary: string; isActive: boolean };

const EMPTY_FORM: FormData = { title: "", description: "", date: "", imageUrl: "", isUpcoming: true, type: "event" };
const EMPTY_VACANCY_FORM: VacancyForm = { title: "", description: "", requirements: "", salary: "", isActive: true };

const TYPE_LABELS: Record<string, string> = { event: "Мероприятие", promo: "Акция" };
const EVENT_TYPE_LABELS: Record<string, string> = {
  birthday: "День рождения", corporate: "Корпоратив", wedding: "Свадьба", other: "Другое",
};
const APP_STATUS_LABELS: Record<string, string> = { new: "Новый", viewed: "Просмотрен", hired: "Принят", rejected: "Отказ" };

function api(path: string, pw: string, options: RequestInit = {}) {
  return fetch(`/api/admin${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${pw}`, ...options.headers },
  });
}

function apiBase(path: string, pw: string, options: RequestInit = {}) {
  return fetch(`/api${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${pw}`, ...options.headers },
  });
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

type AdminTab = "banquet" | "event" | "promo" | "vacancies" | "applications";

export default function Admin() {
  const [password, setPassword] = useState(() => sessionStorage.getItem("admin_pw") || "");
  const [pwInput, setPwInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");

  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [banquets, setBanquets] = useState<BanquetRequest[]>([]);
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [applications, setApplications] = useState<VacancyApplication[]>([]);

  const [tab, setTab] = useState<AdminTab>("banquet");
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [vacancyForm, setVacancyForm] = useState<VacancyForm>(EMPTY_VACANCY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [expandedBanquet, setExpandedBanquet] = useState<number | null>(null);
  const [expandedApp, setExpandedApp] = useState<number | null>(null);
  const [error, setError] = useState("");

  const loadEvents = useCallback(async (pw: string) => {
    const res = await api("/events", pw);
    if (res.ok) { setEvents(await res.json()); setAuthed(true); }
    else { setAuthed(false); }
  }, []);

  const loadBanquets = useCallback(async (pw: string) => {
    const res = await api("/banquet-requests", pw);
    if (res.ok) setBanquets(await res.json());
  }, []);

  const loadVacancies = useCallback(async (pw: string) => {
    const res = await apiBase("/admin/vacancies", pw);
    if (res.ok) setVacancies(await res.json());
  }, []);

  const loadApplications = useCallback(async (pw: string) => {
    const res = await apiBase("/admin/vacancy-applications", pw);
    if (res.ok) setApplications(await res.json());
  }, []);

  useEffect(() => {
    if (password) {
      loadEvents(password);
      loadBanquets(password);
      loadVacancies(password);
      loadApplications(password);
    }
  }, [password, loadEvents, loadBanquets, loadVacancies, loadApplications]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setAuthError("");
    const res = await api("/events", pwInput);
    if (res.ok) {
      setEvents(await res.json());
      sessionStorage.setItem("admin_pw", pwInput);
      setPassword(pwInput);
      setAuthed(true);
      loadBanquets(pwInput);
      loadVacancies(pwInput);
      loadApplications(pwInput);
    } else { setAuthError("Неверный пароль"); }
  }

  function logout() {
    sessionStorage.removeItem("admin_pw");
    setPassword(""); setAuthed(false); setPwInput("");
  }

  // Events
  function openCreate() {
    setForm({ ...EMPTY_FORM, type: tab === "banquet" || tab === "vacancies" || tab === "applications" ? "event" : tab });
    setEditingId(null); setShowForm(true); setError("");
  }

  function openEdit(ev: AdminEvent) {
    setForm({ title: ev.title, description: ev.description, date: ev.date, imageUrl: ev.imageUrl, isUpcoming: ev.isUpcoming, type: ev.type });
    setEditingId(ev.id); setShowForm(true); setError("");
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const res = editingId
        ? await api(`/events/${editingId}`, password, { method: "PUT", body: JSON.stringify(form) })
        : await api("/events", password, { method: "POST", body: JSON.stringify(form) });
      if (!res.ok) { const err = await res.json(); setError(err.error || "Ошибка сохранения"); return; }
      await loadEvents(password); setShowForm(false);
    } finally { setSaving(false); }
  }

  async function handleDelete(id: number) {
    await api(`/events/${id}`, password, { method: "DELETE" });
    setDeleteConfirm(null); await loadEvents(password);
  }

  async function handleDeleteBanquet(id: number) {
    await api(`/banquet-requests/${id}`, password, { method: "DELETE" });
    setDeleteConfirm(null); setBanquets(b => b.filter(r => r.id !== id));
  }

  // Vacancies
  function openCreateVacancy() {
    setVacancyForm(EMPTY_VACANCY_FORM); setEditingId(null); setShowForm(true); setError("");
  }

  function openEditVacancy(v: Vacancy) {
    setVacancyForm({ title: v.title, description: v.description, requirements: v.requirements, salary: v.salary, isActive: v.isActive });
    setEditingId(v.id); setShowForm(true); setError("");
  }

  async function handleSaveVacancy(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const res = editingId
        ? await apiBase(`/admin/vacancies/${editingId}`, password, { method: "PUT", body: JSON.stringify(vacancyForm) })
        : await apiBase("/admin/vacancies", password, { method: "POST", body: JSON.stringify(vacancyForm) });
      if (!res.ok) { const err = await res.json(); setError(err.error || "Ошибка сохранения"); return; }
      await loadVacancies(password); setShowForm(false);
    } finally { setSaving(false); }
  }

  async function handleDeleteVacancy(id: number) {
    await apiBase(`/admin/vacancies/${id}`, password, { method: "DELETE" });
    setDeleteConfirm(null); await loadVacancies(password);
  }

  async function handleUpdateAppStatus(id: number, status: string) {
    await apiBase(`/admin/vacancy-applications/${id}`, password, { method: "PUT", body: JSON.stringify({ status }) });
    setApplications(a => a.map(ap => ap.id === id ? { ...ap, status } : ap));
  }

  async function handleDeleteApp(id: number) {
    await apiBase(`/admin/vacancy-applications/${id}`, password, { method: "DELETE" });
    setDeleteConfirm(null); setApplications(a => a.filter(ap => ap.id !== id));
  }

  const filtered = events.filter((e) => (e.type || "event") === tab);
  const newAppsCount = applications.filter(a => a.status === "new").length;

  if (!authed) {
    return (
      <div className="min-h-screen bg-[hsl(166_42%_3%)] flex items-center justify-center px-4 dark">
        <div className="w-full max-w-sm border border-[hsl(166_100%_32%/0.3)] bg-[hsl(166_38%_6%)] p-10 relative">
          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[hsl(166_100%_32%/0.4)]" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[hsl(166_100%_32%/0.4)]" />
          <div className="flex items-center gap-3 mb-8">
            <div className="w-5 h-[1px] bg-[hsl(166_100%_32%)]" />
            <span className="font-['Cormorant_SC'] text-sm tracking-[0.25em] text-[hsl(166_100%_32%)] uppercase">Паруса на крыше</span>
          </div>
          <h1 className="text-2xl font-serif font-light text-white mb-2">Вход в админку</h1>
          <p className="text-[hsl(166_12%_65%)] text-sm mb-8">Управление банкетными заявками и афишей</p>
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Пароль</label>
              <input type="password" value={pwInput} onChange={(e) => setPwInput(e.target.value)}
                className="w-full bg-transparent border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors text-base"
                placeholder="••••••••" autoFocus />
              {authError && <p className="text-red-400 text-xs mt-2">{authError}</p>}
            </div>
            <button type="submit" className="w-full bg-[hsl(166_100%_32%)] text-white py-3 text-[10px] tracking-[0.3em] uppercase hover:bg-[hsl(166_100%_28%)] transition-colors">Войти</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(166_42%_3%)] text-white dark">
      <header className="border-b border-[hsl(166_100%_32%/0.2)] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-5 h-[1px] bg-[hsl(166_100%_32%)]" />
          <span className="font-['Cormorant_SC'] text-sm tracking-[0.25em] text-[hsl(166_100%_32%)] uppercase">Паруса на крыше</span>
          <span className="text-[hsl(166_12%_65%)] text-xs tracking-[0.1em] uppercase">/ Панель управления</span>
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-[hsl(166_12%_65%)] hover:text-white transition-colors text-xs">
          <LogOut className="w-4 h-4" /> Выйти
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex flex-wrap gap-0 border border-[hsl(166_100%_32%/0.3)]">
            {([
              { id: "banquet" as AdminTab, label: "Банкеты", badge: banquets.length },
              { id: "event" as AdminTab, label: "Мероприятия", badge: 0 },
              { id: "promo" as AdminTab, label: "Акции", badge: 0 },
              { id: "vacancies" as AdminTab, label: "Вакансии", badge: 0 },
              { id: "applications" as AdminTab, label: "Отклики", badge: newAppsCount },
            ] as const).map((t) => (
              <button key={t.id} onClick={() => { setTab(t.id); setShowForm(false); setEditingId(null); }}
                className={`px-5 py-2.5 text-[10px] tracking-[0.2em] uppercase transition-colors relative border-r border-[hsl(166_100%_32%/0.2)] last:border-r-0 ${
                  tab === t.id ? "bg-[hsl(166_100%_32%)] text-white" : "text-[hsl(166_12%_65%)] hover:text-white"
                }`}>
                {t.label}
                {t.badge > 0 && (
                  <span className="ml-1.5 bg-accent text-background text-[8px] rounded-full px-1.5 py-0.5 font-medium">{t.badge}</span>
                )}
              </button>
            ))}
          </div>
          {(tab === "event" || tab === "promo") && (
            <button onClick={openCreate}
              className="flex items-center gap-2 border border-[hsl(166_100%_32%/0.5)] text-[hsl(166_60%_54%)] px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase hover:bg-[hsl(166_100%_32%)] hover:text-white transition-colors">
              <Plus className="w-3.5 h-3.5" /> Добавить
            </button>
          )}
          {tab === "vacancies" && (
            <button onClick={openCreateVacancy}
              className="flex items-center gap-2 border border-[hsl(166_100%_32%/0.5)] text-[hsl(166_60%_54%)] px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase hover:bg-[hsl(166_100%_32%)] hover:text-white transition-colors">
              <Plus className="w-3.5 h-3.5" /> Добавить вакансию
            </button>
          )}
        </div>

        {/* ─── БАНКЕТЫ ─── */}
        {tab === "banquet" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl font-light text-white">Заявки на банкет</h2>
              <button onClick={() => loadBanquets(password)}
                className="text-[9px] tracking-[0.2em] uppercase text-[hsl(166_60%_54%)] border border-[hsl(166_100%_32%/0.3)] px-4 py-2 hover:bg-[hsl(166_100%_32%/0.2)] transition-colors">
                Обновить
              </button>
            </div>
            {banquets.length === 0 ? (
              <div className="border border-[hsl(166_100%_32%/0.15)] p-12 text-center text-[hsl(166_12%_45%)] text-sm tracking-[0.2em] uppercase">Заявок пока нет</div>
            ) : (
              <div className="border border-[hsl(166_100%_32%/0.15)] divide-y divide-[hsl(166_100%_32%/0.08)]">
                {banquets.map((req) => (
                  <div key={req.id} className="px-6 py-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <span className="font-serif text-lg font-light text-white">{req.name}</span>
                          <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border border-[hsl(166_100%_32%/0.4)] text-[hsl(166_60%_54%)]">
                            {EVENT_TYPE_LABELS[req.eventType] ?? req.eventType}
                          </span>
                          <span className="text-[9px] tracking-[0.15em] text-[hsl(166_12%_40%)] uppercase">#{req.id} · {formatDate(req.createdAt)}</span>
                        </div>
                        <div className="flex flex-wrap gap-6 text-sm text-[hsl(166_12%_65%)]">
                          <span><span className="text-[9px] tracking-[0.15em] uppercase text-[hsl(166_12%_40%)] mr-2">Телефон</span>
                            <a href={`tel:${req.phone}`} className="text-[hsl(166_60%_54%)] hover:text-white transition-colors">{req.phone}</a></span>
                          <span><span className="text-[9px] tracking-[0.15em] uppercase text-[hsl(166_12%_40%)] mr-2">Дата</span>{req.date || "—"}</span>
                          <span><span className="text-[9px] tracking-[0.15em] uppercase text-[hsl(166_12%_40%)] mr-2">Гостей</span>{req.guests}</span>
                        </div>
                        {req.comment && (
                          <div className="mt-3">
                            <button onClick={() => setExpandedBanquet(expandedBanquet === req.id ? null : req.id)}
                              className="flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase text-[hsl(166_12%_50%)] hover:text-white transition-colors">
                              {expandedBanquet === req.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Комментарий
                            </button>
                            {expandedBanquet === req.id && (
                              <p className="mt-2 text-sm text-[hsl(166_12%_65%)] border-l-2 border-[hsl(166_100%_32%/0.3)] pl-3 italic">{req.comment}</p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {deleteConfirm === req.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleDeleteBanquet(req.id)} className="bg-red-600 text-white px-3 py-2 text-[9px] tracking-[0.15em] uppercase hover:bg-red-700 transition-colors">Удалить</button>
                            <button onClick={() => setDeleteConfirm(null)} className="border border-[hsl(166_12%_35%)] text-[hsl(166_12%_55%)] px-3 py-2 text-[9px] uppercase hover:text-white transition-colors">Нет</button>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirm(req.id)} className="border border-red-800/40 text-red-500/70 px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:border-red-600 hover:text-red-400 transition-colors">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-[hsl(166_12%_35%)] text-xs tracking-[0.1em]">Заявки сохраняются в базе данных. После обработки можно удалить.</p>
          </div>
        )}

        {/* ─── МЕРОПРИЯТИЯ / АКЦИИ ─── */}
        {(tab === "event" || tab === "promo") && (
          <>
            {showForm && (
              <div className="border border-[hsl(166_100%_32%/0.3)] bg-[hsl(166_38%_6%)] p-8 mb-8 relative">
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[hsl(166_100%_32%/0.4)]" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[hsl(166_100%_32%/0.4)]" />
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-xl font-light">{editingId ? "Редактировать" : "Добавить"} {TYPE_LABELS[form.type] || ""}</h2>
                  <button onClick={() => setShowForm(false)} className="text-[hsl(166_12%_65%)] hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleSave} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Название *</label>
                      <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full bg-transparent border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors" placeholder="Название мероприятия" />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Дата и время *</label>
                      <input required type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                        className="w-full bg-transparent border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors [color-scheme:dark]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Описание *</label>
                    <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full bg-transparent border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors resize-none" placeholder="Описание для страницы Афиши" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Ссылка на фото (URL)</label>
                      <input value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                        className="w-full bg-transparent border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors text-sm" placeholder="https://..." />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Тип</label>
                      <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                        className="w-full bg-[hsl(166_38%_6%)] border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors cursor-pointer">
                        <option value="event">Мероприятие</option>
                        <option value="promo">Акция</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    <button type="button" onClick={() => setForm({ ...form, isUpcoming: !form.isUpcoming })}
                      className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${form.isUpcoming ? "bg-[hsl(166_100%_32%)]" : "bg-[hsl(166_12%_25%)]"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.isUpcoming ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                    <span className="text-sm text-[hsl(166_12%_65%)]">{form.isUpcoming ? "Актуально (показывается в афише)" : "Архив (скрыто)"}</span>
                  </div>
                  {error && <p className="text-red-400 text-sm">{error}</p>}
                  <div className="flex gap-4 pt-2">
                    <button type="submit" disabled={saving}
                      className="flex items-center gap-2 bg-[hsl(166_100%_32%)] text-white px-8 py-3 text-[10px] tracking-[0.25em] uppercase hover:bg-[hsl(166_100%_28%)] transition-colors disabled:opacity-50">
                      <Check className="w-3.5 h-3.5" />{saving ? "Сохранение..." : "Сохранить"}
                    </button>
                    <button type="button" onClick={() => setShowForm(false)}
                      className="border border-[hsl(166_100%_32%/0.3)] text-[hsl(166_12%_65%)] px-8 py-3 text-[10px] tracking-[0.25em] uppercase hover:text-white transition-colors">
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            )}
            <div className="border border-[hsl(166_100%_32%/0.15)]">
              {filtered.length === 0 && (
                <div className="p-12 text-center text-[hsl(166_12%_45%)] text-sm tracking-[0.2em] uppercase">Нет записей. Нажмите «Добавить».</div>
              )}
              {filtered.map((ev, i) => (
                <div key={ev.id} className={`flex flex-col md:flex-row md:items-center gap-4 px-6 py-5 ${i > 0 ? "border-t border-[hsl(166_100%_32%/0.1)]" : ""} ${!ev.isUpcoming ? "opacity-50" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <span className="font-serif text-lg font-light text-white truncate">{ev.title}</span>
                      <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border ${ev.isUpcoming ? "border-[hsl(166_100%_32%/0.5)] text-[hsl(166_60%_54%)]" : "border-[hsl(166_12%_35%)] text-[hsl(166_12%_45%)]"}`}>
                        {ev.isUpcoming ? "Актуально" : "Архив"}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-[hsl(166_12%_55%)]">
                      <span>{ev.date?.replace("T", " в ") || "—"}</span>
                      <span className="text-[hsl(166_12%_40%)]">·</span>
                      <span>{ev.description.slice(0, 80)}{ev.description.length > 80 ? "…" : ""}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => openEdit(ev)} className="flex items-center gap-1.5 border border-[hsl(166_100%_32%/0.3)] text-[hsl(166_60%_54%)] px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:bg-[hsl(166_100%_32%)] hover:text-white transition-colors">
                      <Pencil className="w-3 h-3" /> Ред.
                    </button>
                    {deleteConfirm === ev.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleDelete(ev.id)} className="bg-red-600 text-white px-3 py-2 text-[9px] tracking-[0.15em] uppercase hover:bg-red-700 transition-colors">Удалить</button>
                        <button onClick={() => setDeleteConfirm(null)} className="border border-[hsl(166_12%_35%)] text-[hsl(166_12%_55%)] px-3 py-2 text-[9px] uppercase hover:text-white transition-colors">Нет</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeleteConfirm(ev.id)} className="border border-red-800/40 text-red-500/70 px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:border-red-600 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-[hsl(166_12%_35%)] text-xs text-center tracking-[0.1em]">Изменения отображаются на сайте мгновенно</p>
          </>
        )}

        {/* ─── ВАКАНСИИ ─── */}
        {tab === "vacancies" && (
          <>
            {showForm && (
              <div className="border border-[hsl(166_100%_32%/0.3)] bg-[hsl(166_38%_6%)] p-8 mb-8 relative">
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[hsl(166_100%_32%/0.4)]" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[hsl(166_100%_32%/0.4)]" />
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-xl font-light">{editingId ? "Редактировать вакансию" : "Новая вакансия"}</h2>
                  <button onClick={() => setShowForm(false)} className="text-[hsl(166_12%_65%)] hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleSaveVacancy} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Должность *</label>
                      <input required value={vacancyForm.title} onChange={e => setVacancyForm({ ...vacancyForm, title: e.target.value })}
                        className="w-full bg-transparent border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors"
                        placeholder="Официант, Бармен…" />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Зарплата</label>
                      <input value={vacancyForm.salary} onChange={e => setVacancyForm({ ...vacancyForm, salary: e.target.value })}
                        className="w-full bg-transparent border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors"
                        placeholder="от 80 000 ₽" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Описание *</label>
                    <textarea required rows={3} value={vacancyForm.description} onChange={e => setVacancyForm({ ...vacancyForm, description: e.target.value })}
                      className="w-full bg-transparent border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors resize-none"
                      placeholder="Чем предстоит заниматься…" />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase text-[hsl(166_12%_65%)] mb-2">Требования</label>
                    <textarea rows={2} value={vacancyForm.requirements} onChange={e => setVacancyForm({ ...vacancyForm, requirements: e.target.value })}
                      className="w-full bg-transparent border-b border-[hsl(166_100%_32%/0.3)] pb-2 text-white focus:outline-none focus:border-[hsl(166_100%_32%)] transition-colors resize-none"
                      placeholder="Опыт, навыки, условия…" />
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    <button type="button" onClick={() => setVacancyForm({ ...vacancyForm, isActive: !vacancyForm.isActive })}
                      className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${vacancyForm.isActive ? "bg-[hsl(166_100%_32%)]" : "bg-[hsl(166_12%_25%)]"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${vacancyForm.isActive ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                    <span className="text-sm text-[hsl(166_12%_65%)]">{vacancyForm.isActive ? "Опубликована (видна на сайте)" : "Скрыта"}</span>
                  </div>
                  {error && <p className="text-red-400 text-sm">{error}</p>}
                  <div className="flex gap-4 pt-2">
                    <button type="submit" disabled={saving}
                      className="flex items-center gap-2 bg-[hsl(166_100%_32%)] text-white px-8 py-3 text-[10px] tracking-[0.25em] uppercase hover:bg-[hsl(166_100%_28%)] transition-colors disabled:opacity-50">
                      <Check className="w-3.5 h-3.5" />{saving ? "Сохранение..." : "Сохранить"}
                    </button>
                    <button type="button" onClick={() => setShowForm(false)}
                      className="border border-[hsl(166_100%_32%/0.3)] text-[hsl(166_12%_65%)] px-8 py-3 text-[10px] tracking-[0.25em] uppercase hover:text-white transition-colors">
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="border border-[hsl(166_100%_32%/0.15)]">
              {vacancies.length === 0 && (
                <div className="p-12 text-center text-[hsl(166_12%_45%)] text-sm tracking-[0.2em] uppercase">Нет вакансий. Нажмите «Добавить вакансию».</div>
              )}
              {vacancies.map((v, i) => (
                <div key={v.id} className={`flex flex-col md:flex-row md:items-center gap-4 px-6 py-5 ${i > 0 ? "border-t border-[hsl(166_100%_32%/0.1)]" : ""} ${!v.isActive ? "opacity-50" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <span className="font-serif text-lg font-light text-white">{v.title}</span>
                      {v.salary && <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border border-[hsl(166_100%_32%/0.4)] text-[hsl(166_60%_54%)]">{v.salary}</span>}
                      <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border ${v.isActive ? "border-green-700/50 text-green-400" : "border-[hsl(166_12%_35%)] text-[hsl(166_12%_45%)]"}`}>
                        {v.isActive ? "Опубликована" : "Скрыта"}
                      </span>
                    </div>
                    <p className="text-xs text-[hsl(166_12%_55%)]">{v.description.slice(0, 100)}{v.description.length > 100 ? "…" : ""}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => openEditVacancy(v)}
                      className="flex items-center gap-1.5 border border-[hsl(166_100%_32%/0.3)] text-[hsl(166_60%_54%)] px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:bg-[hsl(166_100%_32%)] hover:text-white transition-colors">
                      <Pencil className="w-3 h-3" /> Ред.
                    </button>
                    {deleteConfirm === v.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleDeleteVacancy(v.id)} className="bg-red-600 text-white px-3 py-2 text-[9px] tracking-[0.15em] uppercase hover:bg-red-700 transition-colors">Удалить</button>
                        <button onClick={() => setDeleteConfirm(null)} className="border border-[hsl(166_12%_35%)] text-[hsl(166_12%_55%)] px-3 py-2 text-[9px] uppercase hover:text-white transition-colors">Нет</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeleteConfirm(v.id)} className="border border-red-800/40 text-red-500/70 px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:border-red-600 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ─── ОТКЛИКИ ─── */}
        {tab === "applications" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl font-light text-white">
                Отклики на вакансии
                {newAppsCount > 0 && <span className="ml-3 text-sm text-accent font-sans font-normal">({newAppsCount} новых)</span>}
              </h2>
              <button onClick={() => loadApplications(password)}
                className="text-[9px] tracking-[0.2em] uppercase text-[hsl(166_60%_54%)] border border-[hsl(166_100%_32%/0.3)] px-4 py-2 hover:bg-[hsl(166_100%_32%/0.2)] transition-colors">
                Обновить
              </button>
            </div>
            {applications.length === 0 ? (
              <div className="border border-[hsl(166_100%_32%/0.15)] p-12 text-center text-[hsl(166_12%_45%)] text-sm tracking-[0.2em] uppercase">Откликов пока нет</div>
            ) : (
              <div className="border border-[hsl(166_100%_32%/0.15)] divide-y divide-[hsl(166_100%_32%/0.08)]">
                {applications.map((ap) => (
                  <div key={ap.id} className={`px-6 py-5 ${ap.status === "new" ? "" : "opacity-70"}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <span className="font-serif text-lg font-light text-white">{ap.name}</span>
                          <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border border-[hsl(166_100%_32%/0.4)] text-[hsl(166_60%_54%)]">{ap.vacancyTitle}</span>
                          <span className={`text-[8px] tracking-[0.15em] uppercase px-2 py-0.5 border ${
                            ap.status === "new" ? "border-accent/50 text-accent" :
                            ap.status === "hired" ? "border-green-700/50 text-green-400" :
                            ap.status === "rejected" ? "border-red-800/40 text-red-400" :
                            "border-[hsl(166_12%_35%)] text-[hsl(166_12%_45%)]"
                          }`}>{APP_STATUS_LABELS[ap.status] ?? ap.status}</span>
                          <span className="text-[9px] tracking-[0.15em] text-[hsl(166_12%_40%)] uppercase">#{ap.id} · {formatDate(ap.createdAt)}</span>
                        </div>
                        <div className="flex flex-wrap gap-6 text-sm text-[hsl(166_12%_65%)]">
                          <span><span className="text-[9px] tracking-[0.15em] uppercase text-[hsl(166_12%_40%)] mr-2">Тел.</span>
                            <a href={`tel:${ap.phone}`} className="text-[hsl(166_60%_54%)] hover:text-white transition-colors">{ap.phone}</a></span>
                          {ap.email && <span><span className="text-[9px] tracking-[0.15em] uppercase text-[hsl(166_12%_40%)] mr-2">Email</span>{ap.email}</span>}
                        </div>
                        {ap.comment && (
                          <div className="mt-3">
                            <button onClick={() => setExpandedApp(expandedApp === ap.id ? null : ap.id)}
                              className="flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase text-[hsl(166_12%_50%)] hover:text-white transition-colors">
                              {expandedApp === ap.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} О себе
                            </button>
                            {expandedApp === ap.id && (
                              <p className="mt-2 text-sm text-[hsl(166_12%_65%)] border-l-2 border-[hsl(166_100%_32%/0.3)] pl-3">{ap.comment}</p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        <select value={ap.status} onChange={e => handleUpdateAppStatus(ap.id, e.target.value)}
                          className="bg-[hsl(166_38%_6%)] border border-[hsl(166_100%_32%/0.3)] text-[hsl(166_12%_65%)] text-[9px] tracking-[0.15em] uppercase px-3 py-2 cursor-pointer focus:outline-none hover:border-[hsl(166_60%_54%)] transition-colors">
                          {Object.entries(APP_STATUS_LABELS).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                        </select>
                        {deleteConfirm === ap.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleDeleteApp(ap.id)} className="bg-red-600 text-white px-3 py-2 text-[9px] tracking-[0.15em] uppercase hover:bg-red-700 transition-colors">Удалить</button>
                            <button onClick={() => setDeleteConfirm(null)} className="border border-[hsl(166_12%_35%)] text-[hsl(166_12%_55%)] px-3 py-2 text-[9px] uppercase hover:text-white transition-colors">Нет</button>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirm(ap.id)} className="border border-red-800/40 text-red-500/70 px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:border-red-600 hover:text-red-400 transition-colors">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-[hsl(166_12%_35%)] text-xs tracking-[0.1em]">Статус отклика меняется в выпадающем списке. Удалённые отклики не восстанавливаются.</p>
          </div>
        )}
      </div>
    </div>
  );
}
